import { useState } from 'react';
import type { ExternalFile } from '@/lib/types';
import AddFilesControls from './AddFilesControls';
import GenericImageUploadErrorDialog from './GenericImageUploadErrorDialog';
import useAggregatedLoadingState from './AddFilesControls/useAggregatedLoadingState';
import useUploadImages from './useUploadImages';
import getSavingState from './getSavingState';
import useAddExternalFiles from './useAddExternalFiles';
import GenericExternalFilesAddErrorDialog from './GenericExternalFilesAddErrorDialog';
import { blockPageClose, unblockPageClose } from '@/lib/pageCloseBlocking';

const AddFiles = () => {
  const [imagesSaving, registerImagesSaveStart, registerImagesSaveEnd] =
    useAggregatedLoadingState();
  const [showGenericImageUploadError, setShowGenericImageUploadError] =
    useState(false);
  const [
    genericUploadErrorInvolvesMultipleFiles,
    setGenericUploadErrorInvolvesMultipleFiles,
  ] = useState(false);

  const uploadImages = useUploadImages();

  const handleUploadImages = async (imageFiles: File[]): Promise<void> => {
    setShowGenericImageUploadError(false);
    setGenericUploadErrorInvolvesMultipleFiles(false);

    const pageCloseBlockerId = blockPageClose();
    const loaderId = registerImagesSaveStart();

    try {
      await uploadImages(imageFiles);
    } catch {
      setGenericUploadErrorInvolvesMultipleFiles(imageFiles.length > 1);
      setShowGenericImageUploadError(true);
    } finally {
      registerImagesSaveEnd(loaderId);
      unblockPageClose(pageCloseBlockerId);
    }
  };

  const [
    externalFilesSaving,
    registerExternalFilesSaveStart,
    registerExternalFilesSaveEnd,
  ] = useAggregatedLoadingState();
  const [
    showGenericExternalFilesAddError,
    setShowGenericExternalFilesAddError,
  ] = useState(false);
  const [
    genericExternalFilesAddErrorInvolvesMultipleFiles,
    setGenericExternalFilesAddErrorInvolvesMultipleFiles,
  ] = useState(false);

  const addExternalFiles = useAddExternalFiles();

  const handleAddExternalFiles = async (
    externalFiles: ExternalFile[],
  ): Promise<void> => {
    setShowGenericExternalFilesAddError(false);
    setGenericExternalFilesAddErrorInvolvesMultipleFiles(false);

    const pageCloseBlockerId = blockPageClose();
    const loaderId = registerExternalFilesSaveStart();

    try {
      await addExternalFiles(externalFiles);
    } catch {
      setGenericExternalFilesAddErrorInvolvesMultipleFiles(
        externalFiles.length > 1,
      );
      setShowGenericExternalFilesAddError(true);
    } finally {
      registerExternalFilesSaveEnd(loaderId);
      unblockPageClose(pageCloseBlockerId);
    }
  };

  const savingState = getSavingState(imagesSaving, externalFilesSaving);

  return (
    <>
      <AddFilesControls
        savingState={savingState}
        onUploadImages={handleUploadImages}
        onAddExternalFiles={handleAddExternalFiles}
      />
      <GenericImageUploadErrorDialog
        open={showGenericImageUploadError}
        attemptedUploadInvolvesMultipleFiles={
          genericUploadErrorInvolvesMultipleFiles
        }
        onClose={() => {
          setShowGenericImageUploadError(false);

          // Wait for the dialog animation to finish before changing the
          // singular vs plural message style. This prevents the text from
          // shifting before the dialog is closed.
          setTimeout(() => {
            setGenericUploadErrorInvolvesMultipleFiles(false);
          }, 250);
        }}
      />
      <GenericExternalFilesAddErrorDialog
        open={showGenericExternalFilesAddError}
        attemptedAddInvolvesMultipleFiles={
          genericExternalFilesAddErrorInvolvesMultipleFiles
        }
        onClose={() => {
          setShowGenericExternalFilesAddError(false);

          setTimeout(() => {
            setGenericExternalFilesAddErrorInvolvesMultipleFiles(false);
          }, 250);
        }}
      />
    </>
  );
};

export default AddFiles;
